import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { KnowledgeSource, KnowledgeSourceKey } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../ai-knowledge.service';
import { AppsForKnowledgeSourceComponent } from '../apps-for-knowledge-source/apps-for-knowledge-source.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AddKnowledgeComponent } from './add-knowledge/add-knowledge.component';
import { WebsiteScrapeComponent } from './website-scrape/website-scrape.component';
import { CustomDataComponent } from './custom-data/custom-data.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { FeatureFlagService } from '@galaxy/partner';
import { PARTNER_ID_TOKEN } from '../tokens';
import { CommonKnowledgeSourceEditorComponent } from './common/common-knowledge-source-editor.component';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import {
  EDIT_OPTION as SCRAPED_PAGES_EDIT_OPTION,
  ScrapedPagesComponent,
} from './website-scrape/scraped-pages/scraped-pages.component';

// feature flag id
export const WEBSITE_KNOWLEDGE = 'website_knowledge';

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  readOnly?: boolean;
  editOption?: string;
}

@Component({
  selector: 'ai-knowledge-knowledge-editor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTabsModule,
    MatSelectModule,
    MatOptionModule,
    MatExpansionModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    RouterModule,
    AppsForKnowledgeSourceComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './knowledge-editor.component.html',
  styleUrls: ['./knowledge-editor.component.scss'],
})
export class KnowledgeEditorComponent implements OnInit {
  @ViewChild('component', { read: ViewContainerRef }) componentViewRef?: ViewContainerRef;
  componentInstance: CommonKnowledgeSourceEditorComponent;
  private readonly knowledgeService = inject(AiKnowledgeService);
  private featureFlagService = inject(FeatureFlagService);
  private cdr = inject(ChangeDetectorRef);
  private confirmationModal = inject(OpenConfirmationModalService);
  private readonly activeRoute = inject(ActivatedRoute);
  router = inject(Router);
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  readonly isLoading = signal(true);
  component: Type<CommonKnowledgeSourceEditorComponent>;
  knowledgeSource: KnowledgeSource;

  constructor(
    private readonly destroyRef: DestroyRef,
    public dialogRef: MatDialogRef<KnowledgeEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
  ) {}

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;
    if (this.data?.knowledgeSourceKey) {
      this.knowledgeSource = await this.knowledgeService.getKnowledgeSource(
        this.data.knowledgeSourceKey.knowledgeSourceId,
        this.data.knowledgeSourceKey.namespace,
      );
    }
    await this.setInitialComponent();
    this.isLoading.set(false);
  }

  setEditSourceQueryParam(editSource: string | null, option?: string): void {
    if (option) {
      editSource = editSource + ':' + option;
    }
    const queryParams = { editSource: editSource };
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  async websiteKnowledgeFeatureEnabled(): Promise<boolean> {
    const results = await firstValueFrom(
      this.featureFlagService.batchGetStatus(this.partnerId(), '', [WEBSITE_KNOWLEDGE]),
    );
    return results[WEBSITE_KNOWLEDGE];
  }

  close(): void {
    this.warnUnsavedChanged(() => {
      this.setEditSourceQueryParam(null);
      this.dialogRef.close();
    });
  }

  async setInitialComponent(): Promise<void> {
    if (this.knowledgeSource) {
      if (this.knowledgeSource.config?.customDataConfig) {
        this.setComponent(CustomDataComponent);
      } else if (this.knowledgeSource.config?.websiteScrapeConfig) {
        if (this.data?.editOption === SCRAPED_PAGES_EDIT_OPTION) {
          this.setComponent(ScrapedPagesComponent);
        } else {
          this.setComponent(WebsiteScrapeComponent);
        }
      }
    } else if (await this.websiteKnowledgeFeatureEnabled()) {
      this.setComponent(AddKnowledgeComponent);
    } else {
      this.setComponent(CustomDataComponent);
    }
  }

  setComponent(c: Type<CommonKnowledgeSourceEditorComponent>): void {
    this.component = c;
    this.componentViewRef.detach();
    this.componentInstance = this.componentViewRef.createComponent(c).instance;
    this.componentInstance.knowledgeSource = this.knowledgeSource;
    this.componentInstance.readOnly = this.data?.readOnly;
    this.componentInstance.init();
    this.componentInstance.cdr.detectChanges();
    this.setEditSourceQueryParam(
      this.data?.knowledgeSourceKey?.knowledgeSourceId,
      this.componentInstance.getEditOption(),
    );
    if (c === AddKnowledgeComponent) {
      (this.componentInstance as AddKnowledgeComponent).componentSelected
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((c) => this.setComponent(c));
    }
  }

  async submit(): Promise<void> {
    if (this.componentInstance) {
      let result = this.knowledgeSource?.id;
      if (this.componentInstance.hasUnsavedChanges()) {
        result = await this.componentInstance.submit();
      }
      if (result) {
        const nextStep = this.componentInstance.nextStep();
        if (nextStep) {
          this.knowledgeSource = await this.knowledgeService.getKnowledgeSource(
            result,
            this.knowledgeService.buildNamespaceInterface(),
          );
          this.setComponent(nextStep);
          this.cdr.detectChanges();
          return;
        }
        this.setEditSourceQueryParam(null);
        this.dialogRef.close(true);
      }
    }
  }

  showBack(): boolean {
    return !!this.componentInstance?.previousStep();
  }

  back(): void {
    this.warnUnsavedChanged(() => {
      if (this.componentInstance?.previousStep()) {
        this.setComponent(this.componentInstance.previousStep());
        this.cdr.detectChanges();
      }
    });
  }

  warnUnsavedChanged(func: () => void): void {
    if (!this.componentInstance?.hasUnsavedChanges()) {
      func();
      return;
    }

    this.confirmationModal
      .openModal({
        title: 'UNSAVED_CHANGES.TITLE',
        message: 'UNSAVED_CHANGES.MESSAGE',
        confirmButtonText: 'COMMON.YES',
        cancelButtonText: 'COMMON.NO',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          func();
        }
      });
  }

  getTitle(): string {
    if (this.data?.readOnly && this.knowledgeSource) {
      return this.knowledgeSource.name;
    }
    if (!this.data?.knowledgeSourceKey) {
      return 'ADD_KNOWLEDGE';
    }
    return 'EDIT_KNOWLEDGE.TITLE';
  }

  buildEditUrl(): string {
    return this.knowledgeService.buildEditKnowledgeUrl(this.knowledgeSource?.id || '');
  }

  showSubmit(): boolean {
    return this.component !== AddKnowledgeComponent;
  }

  submitDisabled(): boolean {
    return this.componentInstance?.disableSubmit;
  }

  getSubmitAction(): string {
    if (this.componentInstance && this.componentInstance.nextStep()) {
      return 'COMMON.SAVE_AND_CONTINUE';
    }
    return 'COMMON.SAVE';
  }
}
